<template>
  <div class="crash-wrapper">
    <div class="overlay-loading" v-show="isWaiting"></div>

    <h2>Crash (Demo)</h2>

    <div v-if="!chosenGame">
      <ul>
        <li v-for="g in games" :key="g.id">
          <button type="button" @click.stop.prevent="chooseGame(g)">
            {{ g.name }} - {{ g.price || "N/A" }} SEK
          </button>
        </li>
      </ul>
    </div>

    <div v-if="chosenGame">
      <h3>{{ chosenGame.name }}</h3>
      <p v-if="roundState === 'pending'">Round starts in: {{ buyInCountdownSeconds }}s</p>
      <p v-else-if="roundState === 'started'">Round is running. Wait for next buy-in window.</p>
      <p v-else>Waiting for buy-in window...</p>

      <div v-if="!currentTicket">
        <label>Bet amount:</label>
        <input type="number" v-model.number="betAmount" @keyup.enter.prevent="buy" />
        <button type="button" :disabled="!canBuyCurrentRound || isWaiting" @click.stop.prevent="buy">Buy</button>
      </div>
    </div>

    <CrashGameView
      v-if="chosenGame"
      :chosenGame="chosenGame"
      :currentTicket="currentTicket"
      @round-state="onRoundStateChanged"
      @ticket-result="onTicketResult"
    />

    <div v-if="purchasedTickets.length > 0" class="ticket-history">
      <h3>Purchased tickets</h3>
      <table>
        <thead>
          <tr>
            <th>Ticket ID</th>
            <th>Bet</th>
            <th>Bought</th>
            <th>Result</th>
            <th>Resolved</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ticket in purchasedTickets" :key="ticket.ticketIdentifier">
            <td>{{ ticket.ticketIdentifier }}</td>
            <td>{{ ticket.wager }}</td>
            <td>{{ ticket.purchasedAtText }}</td>
            <td>{{ ticket.resultText }}</td>
            <td>{{ ticket.resolvedAtText || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import store from "../store.js";

import { unifiedBuy } from '../utils/unifiedBuy.js';
import { getSuggestedWager } from "../utils/gameHelper.js";
import CrashGameView from "./CrashGameView.vue";

export default {
  name: "CrashGame",
  components: {
    CrashGameView,
  },
  data() {
    return {
      games: [],
      chosenGame: null,
      betAmount: 2,
      isWaiting: false,
      currentTicket: null,
      roundState: "idle",
      buyInCountdownSeconds: 0,
      chooseGameDebounceTimer: null,
      purchasedTickets: [],
    };
  },
  computed: {
    canBuyCurrentRound() {
      return this.chosenGame && this.roundState === "pending";
    },
  },
  beforeUnmount() {
    clearTimeout(this.chooseGameDebounceTimer);
  },
  mounted() {
    const crashGames = store.clientConfig?.Crash || [];
    this.games = Array.isArray(crashGames) ? crashGames : [];
  },
  methods: {
    getTicketId(ticket) {
      return ticket?.ticketIdentifier || ticket?.id || ticket?.Id;
    },
    formatTime(dateValue) {
      return dateValue ? new Date(dateValue).toLocaleTimeString("sv-SE", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }) : "";
    },
    chooseGame(g) {
      clearTimeout(this.chooseGameDebounceTimer);
      this.chooseGameDebounceTimer = setTimeout(() => this.applyChosenGame(g), 120);
    },
    applyChosenGame(g) {
      const template = g?.buyTicketMeta?.buyArguments?.[0] || null;
      const nextPluginGameId =
        g?.pluginGameId ||
        g?.PluginGameId ||
        g?.buyTicketMeta?.gameId ||
        g?.buyTicketMeta?.GameId ||
        template?.pluginGameId ||
        template?.PluginGameId ||
        null;

      if (Number(this.chosenGame?.pluginGameId) === Number(nextPluginGameId)) return;

      this.chosenGame = { ...g, pluginGameId: nextPluginGameId };
      this.betAmount = getSuggestedWager(g) || 2;
      this.roundState = "idle";
      this.buyInCountdownSeconds = 0;
    },
    onRoundStateChanged(payload) {
      if (payload) {
        this.roundState = payload.state || "idle";
        this.buyInCountdownSeconds = Math.max(0, payload.countdownSeconds || 0);
      }
    },
    onTicketResult(payload) {
      if (!payload?.ticketIdentifier) return;

      const idx = this.purchasedTickets.findIndex((t) => t.ticketIdentifier === payload.ticketIdentifier);
      if (idx < 0) return;

      const mult = (payload.cashoutMultiplier || payload.finalMultiplier || 0).toFixed(2);
      const resolvedAt = new Date();

      this.purchasedTickets[idx] = {
        ...this.purchasedTickets[idx],
        status: payload.status || "finished",
        finalMultiplier: payload.finalMultiplier,
        cashoutMultiplier: payload.cashoutMultiplier,
        resultText: payload.status === "cashedout" ? `Cashed out at ${mult}x` : `Crashed at ${mult}x`,
        resolvedAt,
        resolvedAtText: this.formatTime(resolvedAt),
      };

      if (this.getTicketId(this.currentTicket) === payload.ticketIdentifier) this.currentTicket = null;
    },
    async buy() {
      if (!this.chosenGame || !this.canBuyCurrentRound) return;
      const template = this.chosenGame?.buyTicketMeta?.buyArguments?.[0];
      if (!template) {
        console.error("Invalid buy template for Crash");
        return;
      }
      const arg = {
        ...template,
        gameType: "Crash",
        pluginGameId: template.pluginGameId || template.crashId || this.chosenGame.pluginGameId,
        wager: this.betAmount,
      };
      await unifiedBuy({
        game: this.chosenGame,
        buyArguments: [arg],
        setLoading: (v) => { this.isWaiting = v; },
        onSuccess: (data) => {
          if (!data?.length) return;
          const rawTicket = data[0]?.tickets?.[0] || data[0];
          const ticketId = this.getTicketId(rawTicket);
          if (!ticketId) return;
          this.currentTicket = { ...rawTicket, ticketIdentifier: ticketId };
          const now = new Date();
          const item = {
            ticketIdentifier: ticketId,
            wager: rawTicket?.buyIn || rawTicket?.wager || arg.wager || this.betAmount,
            status: "running",
            resultText: "Running",
            purchasedAt: now,
            purchasedAtText: this.formatTime(now),
            resolvedAt: null,
            resolvedAtText: "",
          };
          const idx = this.purchasedTickets.findIndex((t) => t.ticketIdentifier === ticketId);
          if (idx >= 0) {
            this.purchasedTickets[idx] = { ...this.purchasedTickets[idx], ...item };
          } else {
            this.purchasedTickets.unshift(item);
          }
        },
        onError: (e) => {
          this.isWaiting = false;
          console.error("buy failed", e);
        }
      });
    },
  },
};
</script>

<style scoped>
.crash-wrapper {
  padding: 20px;
}
.ticket-history {
  margin-top: 24px;
}
.ticket-history table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
}
.ticket-history th,
.ticket-history td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}
.ticket-history th {
  background-color: #f2f2f2;
  font-weight: bold;
}
.ticket-history tr:nth-child(even) {
  background-color: #f9f9f9;
}
.overlay-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1000;
}
</style>
